import { useEffect, useState } from 'react'
import DOMPurify from 'dompurify'
import { RefreshCw, Sparkles } from 'lucide-react'
import { api } from '../../lib/api'
import { formatRelative } from './format'

// The AI insight prompt asks for HTML, so render it through an allowlist of formatting tags.
// No style attribute: the card follows the theme (light/dark) instead of the model's colors.
const INSIGHT_ALLOWED_TAGS = ['p', 'strong', 'em', 'b', 'i', 'ul', 'ol', 'li', 'br', 'span']

function sanitizeInsightHtml(html: string): string {
  return DOMPurify.sanitize(html, { ALLOWED_TAGS: INSIGHT_ALLOWED_TAGS, ALLOWED_ATTR: [] })
}

/**
 * Virtual CISO summary card (#397 step 3d): neutral styling, and provenance so readers know
 * it's AI-generated and when.
 */
export function AIInsightsPanel({ page }: { page: string }) {
  const [loading, setLoading] = useState(false)
  const [insight, setInsight] = useState<string | null>(null)
  const [context, setContext] = useState<{ summary: string; criticalCount: number } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [generatedAt, setGeneratedAt] = useState<number | null>(null)

  async function loadInsight() {
    setLoading(true)
    setError(null)
    try {
      const res = await api.getPageInsight(page)
      if (res.success) {
        setInsight(res.insight)
        setContext(res.context || null)
        setGeneratedAt(res.generatedAt || Date.now())
      } else {
        setError(res.error || 'Failed to generate the summary')
      }
    } catch {
      setError('Failed to connect to the AI service')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadInsight()
  }, [page])

  return (
    <section aria-labelledby="dw-ai-title" className="flex flex-col gap-3 rounded-xl border border-line bg-surface p-5">
      <div className="flex items-center justify-between gap-2">
        <h2 id="dw-ai-title" className="m-0 inline-flex items-center gap-2 font-heading text-base font-semibold text-ink">
          <Sparkles className="h-4 w-4 text-link" aria-hidden="true" />
          Virtual CISO summary
        </h2>
        <button
          type="button"
          onClick={loadInsight}
          disabled={loading}
          aria-label="Regenerate summary"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-secondary hover:bg-surface-raised disabled:opacity-50 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-focus"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
        </button>
      </div>

      {loading && !insight && <p className="m-0 text-sm text-muted" role="status">Analyzing exposures…</p>}
      {error && <p className="m-0 text-sm text-critical">{error}</p>}
      {insight && (
        <div
          className="text-sm leading-relaxed text-ink [&_ol]:ml-5 [&_ol]:list-decimal [&_p]:mb-2 [&_strong]:font-semibold [&_ul]:ml-5 [&_ul]:list-disc"
          dangerouslySetInnerHTML={{ __html: sanitizeInsightHtml(insight) }}
        />
      )}
      {!loading && !error && !insight && <p className="m-0 text-sm text-muted">No summary available yet.</p>}

      {insight && (
        <span className="text-xs font-medium text-muted">
          AI-generated{context?.summary ? ` from ${context.summary}` : ''} · {formatRelative(generatedAt)} · verify before acting
        </span>
      )}
    </section>
  )
}
